import { useState } from "react";
import { Calendar, Paperclip, GripVertical, MessageSquare, CheckCircle2, MoreHorizontal, Trash2 } from "lucide-react";
import { API } from "../lib/api";
import { resolveAssignees } from "../lib/assignees";
import { AssigneeAvatars } from "./AssigneeAvatars";

const PRIORITY = {
  high: { label: "Yüksek", cls: "text-red-600 bg-red-50 dark:bg-red-500/15 dark:text-red-400" },
  medium: { label: "Orta", cls: "text-orange-600 bg-orange-50 dark:bg-orange-500/15 dark:text-orange-400" },
  low: { label: "Düşük", cls: "text-blue-600 bg-blue-50 dark:bg-blue-500/15 dark:text-blue-400" },
};

export const TaskCard = ({ task, board, color, onClick, onDelete }) => {
  const [showMenu, setShowMenu] = useState(false);
  const subDone = (task.subtasks || []).filter((s) => s.done).length;
  const subTotal = (task.subtasks || []).length;
  const prio = PRIORITY[task.priority] || PRIORITY.medium;
  const cover = task.cover_attachment_id && (task.attachments || []).find((a) => a.id === task.cover_attachment_id);
  const people = resolveAssignees(task, board);
  const commentCount = (task.comments || []).length;

  return (
    <div
      data-testid="kanban-card-item"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("taskId", task.id);
        e.dataTransfer.effectAllowed = "move";
        const el = e.currentTarget;
        setTimeout(() => el.classList.add("opacity-40", "rotate-1", "scale-[0.98]"), 0);
      }}
      onDragEnd={(e) => e.currentTarget.classList.remove("opacity-40", "rotate-1", "scale-[0.98]")}
      onClick={onClick}
      className="group relative flex flex-col w-full bg-white dark:bg-[#232326] rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm hover:shadow-md transition-all duration-200 cursor-grab active:cursor-grabbing"
    >
      {cover && (
        <div className="w-full h-32 overflow-hidden rounded-t-2xl border-b border-slate-100 dark:border-white/10">
          <img src={`${API}/files/${cover.storage_path}`} alt="" crossOrigin="use-credentials"
            onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </div>
      )}

      <div className="flex flex-col gap-4 p-5">
        <div className="absolute top-1/2 -left-3 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-slate-300">
          <GripVertical size={16} />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {task.group && (
              <span title={task.group} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold tracking-wide bg-slate-100 dark:bg-white/10 text-slate-700 dark:text-slate-300 uppercase max-w-[180px]">
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
                <span className="truncate">{task.group}</span>
              </span>
            )}
            <span className={`px-2 py-1 rounded-md text-[11px] font-bold tracking-wide uppercase ${prio.cls}`}>{prio.label}</span>
          </div>
          <div className="relative">
            <button data-testid="kanban-card-menu-button"
              onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
              className="text-slate-300 hover:text-slate-600 transition-colors p-1">
              <MoreHorizontal size={18} />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setShowMenu(false); }} />
                <div className="absolute right-0 mt-1 w-32 bg-white dark:bg-[#2C2C2E] border border-slate-100 dark:border-white/10 rounded-lg shadow-xl overflow-hidden z-20 py-1">
                  <button data-testid="kanban-card-delete-button"
                    onClick={(e) => { e.stopPropagation(); setShowMenu(false); onDelete(task); }}
                    className="w-full text-left px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors flex items-center gap-2">
                    <Trash2 size={14} /> Sil
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <h4 className="text-[15px] font-bold text-slate-900 dark:text-white leading-snug">{task.title}</h4>
          {task.description && (
            <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">{task.description}</p>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-white/10">
          <div className="flex items-center gap-3.5 text-xs font-medium text-slate-500">
            {task.due_date && (
              <div className="flex items-center gap-1.5">
                <Calendar size={14} className="text-slate-400" />
                <span>{new Date(task.due_date).toLocaleDateString("tr-TR", { day: "numeric", month: "short" })}</span>
              </div>
            )}
            {subTotal > 0 && (
              <div className="flex items-center gap-1.5">
                <CheckCircle2 size={14} className={subDone === subTotal ? "text-[#34C759]" : "text-slate-400"} />
                <span>{subDone}/{subTotal}</span>
              </div>
            )}
            {commentCount > 0 && (
              <div className="flex items-center gap-1.5">
                <MessageSquare size={14} className="text-slate-400" />
                <span>{commentCount}</span>
              </div>
            )}
            {(task.attachments || []).length > 0 && (
              <div className="flex items-center gap-1.5">
                <Paperclip size={14} className="text-slate-400" />
                <span>{task.attachments.length}</span>
              </div>
            )}
          </div>
          {people.length > 0 && <AssigneeAvatars assignees={people} className="shrink-0 ml-4" />}
        </div>
      </div>
    </div>
  );
};

export default TaskCard;
